import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAudioPlayerContext } from "../contexts/AudioPlayerContext";
import { PLAY, STOP } from "../redux/actions/PlayerActions";
import store from "../redux/store";
import Loader from "./Loader";

const PLAYING = "playing";
const STOPPED = "stopped";
const LOADING = "loading";

const PlayPreviewButton = ({
  voice = "ethan",
  showText = true,
  className = "flex-col",
  textToPlay,
  id,
  audioPath = null,
  iconSize = "size-7",
  disabled = false,
  textSize = "text-[10px] md:text-[12px]",
  preventEventPropagation = false,
  language = "en-US",
}) => {
  const player = useSelector((state) => state.player);
  const dispatch = useDispatch();

  const { playAudio, eventEmitter, stopAudio } = useAudioPlayerContext();
  const [playingStatus, setPlayingStatus] = useState(STOPPED);
  const [playingIcon, setPlayingIcon] = useState(null);
  const audioRef = useRef(null);

  const urlToPlay = useMemo(() => {
    const urlParams = new URLSearchParams({
      text: textToPlay,
      organizationId: store.getState().info.organization.id,
      // language,
    });

    const url = `${window.appConfig.API_GATEWAY_URL}/snippet/voices/${voice}/play?${urlParams.toString()}`;

    return audioPath != null ? audioPath : url;
  }, [textToPlay, voice]);

  const updatePlayingIcon = () => {
    if (playingStatus === PLAYING) {
      setPlayingIcon("/assets/icons/stop-icon.svg");
    } else {
      setPlayingIcon("/assets/icons/play-icon.svg");
    }
  };

  const playPreview = async (event) => {
    if (preventEventPropagation) {
      event.stopPropagation();
    }

    if (playingStatus === STOPPED) {
      setPlayingStatus(LOADING);
      playAudio(audioRef.current, id);
      dispatch({ type: PLAY });

      try {
        // Fetch audio with authorization header
        const token = store.getState().info.token;
        const response = await fetch(urlToPlay, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        audioRef.current.src = blobUrl;
        await audioRef.current.play();
        setPlayingStatus(PLAYING);

        // Clean up blob URL when audio ends
        const cleanupBlob = () => {
          URL.revokeObjectURL(blobUrl);
        };
        audioRef.current.addEventListener("ended", cleanupBlob, { once: true });
        audioRef.current.addEventListener("pause", cleanupBlob, { once: true });
      } catch (error) {
        console.error("Error playing audio:", error);
        setPlayingStatus(STOPPED);
        dispatch({ type: STOP });
      }
    } else if (playingStatus === PLAYING || playingStatus == LOADING) {
      audioRef.current.pause();
      setPlayingStatus(STOPPED);

      dispatch({ type: STOP });
    }
  };

  useEffect(() => {
    if (player.isPlaying) {
      stopAudio();
    }
  }, []);

  useEffect(() => {
    updatePlayingIcon();
  }, [playingStatus]);

  useEffect(() => {
    const stopListener = (stoppedComponentId) => {
      if (id !== stoppedComponentId) {
        setPlayingStatus(STOPPED);
      }
    };

    eventEmitter.setMaxListeners(50);
    eventEmitter.on("stop", stopListener);

    return () => {
      eventEmitter.removeListener("stop", stopListener);
    };
  }, [eventEmitter, id]);

  if (!showText) {
    return (
      <>
        <audio ref={audioRef} className="" onEnded={() => setPlayingStatus(STOPPED)} />
        {playingStatus !== LOADING && (
          <img
            onClick={playPreview}
            src={playingIcon}
            alt="play"
            className={`flex justify-center items-center  md:hover:opacity-70 ${className}
        ${disabled || !textToPlay ? "opacity-50 pointer-events-none" : "cursor-pointer"} ${iconSize}`}
          />
        )}
        {playingStatus === LOADING && <Loader size={28} border="border-4" color={"black"} />}
      </>
    );
  }

  return (
    <div
      onClick={playPreview}
      className={`flex justify-center items-center  md:hover:opacity-70 ${className}
       ${disabled || !textToPlay ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
    >
      <audio ref={audioRef} className="" onEnded={() => setPlayingStatus(STOPPED)} />
      {playingStatus !== LOADING && <img src={playingIcon} alt="play" className={`${iconSize}`} />}
      {playingStatus === LOADING && <Loader size={28} border="border-4" color={"black"} />}
      {showText && <span className={`${textSize} text-black font-medium italic`}>Preview</span>}
    </div>
  );
};

export default PlayPreviewButton;
